import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OvertimeTypeSelect } from '@/components/selects';
import { HrAttendanceSegmentList } from '@/modules/hr/components/HrAttendanceSegmentBreakdown';
import { hrFormatMinutes } from '@/modules/hr/components/hrCalendarUtils';
import { hrEstimateOvertimeSplit } from '@/modules/hr/components/hrOvertimeSplit';

export function estimateAttendanceOvertimeMinutes(
  checkIn: string,
  checkOut: string,
  expectedMinutes: number,
  breakMinutes = 0,
  shiftStartTime?: string | null,
  shiftEndTime?: string | null,
  otherDayRegularMinutes = 0,
): number {
  return hrEstimateOvertimeSplit({
    checkIn,
    checkOut,
    expectedMinutes,
    breakMinutes,
    shiftStartTime,
    shiftEndTime,
    otherDayRegularMinutes,
  }).overtimeMinutes;
}

type Props = {
  checkIn: string;
  checkOut: string;
  overtimeType: string;
  onOvertimeTypeChange: (value: string) => void;
  expectedMinutes?: number;
  breakMinutes?: number;
  /** Scheduled shift window; when known, overtime is the time worked outside it. */
  shiftStartTime?: string | null;
  shiftEndTime?: string | null;
  /** Regular minutes from other same-day punches (day-level expected budget). */
  otherDayRegularMinutes?: number;
};

export function HrAttendanceOvertimeFields({
  checkIn,
  checkOut,
  overtimeType,
  onOvertimeTypeChange,
  expectedMinutes,
  breakMinutes = 0,
  shiftStartTime,
  shiftEndTime,
  otherDayRegularMinutes = 0,
}: Props) {
  const { t } = useTranslation('hr');
  const split = useMemo(
    () =>
      expectedMinutes == null
        ? { overtimeMinutes: 0, segments: [] as ReturnType<typeof hrEstimateOvertimeSplit>['segments'] }
        : hrEstimateOvertimeSplit({
            checkIn,
            checkOut,
            expectedMinutes,
            breakMinutes,
            shiftStartTime,
            shiftEndTime,
            otherDayRegularMinutes,
          }),
    [
      breakMinutes,
      checkIn,
      checkOut,
      expectedMinutes,
      otherDayRegularMinutes,
      shiftEndTime,
      shiftStartTime,
    ],
  );

  const segmentViews = useMemo(
    () =>
      split.segments.map((segment) => ({
        key: `${segment.type}-${segment.start}`,
        segmentType: segment.type,
        start: segment.start,
        end: segment.end,
        minutes: segment.minutes,
        overtime: segment.overtime,
      })),
    [split.segments],
  );

  if (!checkIn || !checkOut || checkOut <= checkIn) {
    return null;
  }

  const expectedLabel = expectedMinutes == null ? null : hrFormatMinutes(expectedMinutes);
  const description =
    expectedLabel == null
      ? t('overtimePage.attendanceFields.schedulePending')
      : split.overtimeMinutes > 0
        ? t('overtimePage.attendanceFields.estimatedWithType', {
            estimated: hrFormatMinutes(split.overtimeMinutes),
            expected: expectedLabel,
          })
        : t('overtimePage.attendanceFields.expectedBeyondSchedule', { expected: expectedLabel });

  return (
    <div className="rounded-lg border border-amber-200/80 bg-amber-50/50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
        {t('overtimePage.attendanceFields.title')}
      </p>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">{description}</p>
      <HrAttendanceSegmentList segments={segmentViews} className="mt-2" />
      <div className="mt-3 max-w-md">
        <OvertimeTypeSelect
          value={overtimeType}
          onChange={onOvertimeTypeChange}
          required={split.overtimeMinutes > 0}
          autoSelectWhenRequired
          placeholder={t('labels.selectOvertimeType')}
        />
      </div>
    </div>
  );
}
